/**
 * Source color management utility
 * Stores customizable colors for each lead source
 */

import { LeadSource } from '../types';

// Default colors for each source
export const DEFAULT_SOURCE_COLORS: Record<LeadSource, string> = {
  'unspecified': '#9AA0A6', // Gray
  'web-form': '#4285F4', // Blue
  'facebook-form': '#1877F2', // Facebook Blue
  'instagram-form': '#E4405F', // Instagram Pink
  'ebook': '#34A853', // Green
  'instagram-follow': '#E4405F', // Instagram Pink
  'facebook-follow': '#1877F2', // Facebook Blue
  'b2b-visit': '#FF6D01', // Orange
  'cold-call': '#EA4335', // Red
  'call-in': '#9334E6', // Purple
  'referral': '#0F9D58', // Dark Green
};

const STORAGE_KEY = 'crm_source_colors';

/**
 * Get all source colors (custom or default)
 */
export const getSourceColors = (): Record<LeadSource, string> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const customColors = JSON.parse(stored);
      // Merge with defaults to ensure all sources have colors
      return { ...DEFAULT_SOURCE_COLORS, ...customColors };
    }
  } catch (error) {
    console.warn('Error loading source colors from localStorage:', error);
  }
  return DEFAULT_SOURCE_COLORS;
};

/**
 * Normalize source string to match LeadSource type
 * Handles: web_form, Web Form, WEB-FORM -> web-form
 * Also handles display names like "Web Form", "Facebook Form"
 */
const normalizeSource = (source: string): LeadSource => {
  if (!source) return 'unspecified';
  
  // Convert to lowercase, replace underscores AND spaces with hyphens
  const normalized = source
    .toLowerCase()
    .trim()
    .replace(/[_\s]+/g, '-')  // underscores and spaces to hyphens
    .replace(/--+/g, '-')      // multiple hyphens to single
    .replace(/^-|-$/g, '');    // trim leading/trailing hyphens
  
  // Map common variations and display names
  const sourceMap: Record<string, LeadSource> = {
    // Standard formats
    'facebook-form': 'facebook-form',
    'facebook': 'facebook-form',
    'fb-form': 'facebook-form',
    'fb': 'facebook-form',
    'instagram-form': 'instagram-form',
    'instagram': 'instagram-form',
    'ig-form': 'instagram-form',
    'ig': 'instagram-form',
    'web-form': 'web-form',
    'webform': 'web-form',
    'website': 'web-form',
    'web': 'web-form',
    'ebook': 'ebook',
    'e-book': 'ebook',
    'instagram-follow': 'instagram-follow',
    'facebook-follow': 'facebook-follow',
    'b2b-visit': 'b2b-visit',
    'b2b': 'b2b-visit',
    'cold-call': 'cold-call',
    'coldcall': 'cold-call',
    'call-in': 'call-in',
    'callin': 'call-in',
    'referral': 'referral',
    'referred': 'referral',
    'unspecified': 'unspecified',
  };
  
  return sourceMap[normalized] || (DEFAULT_SOURCE_COLORS[normalized as LeadSource] ? normalized as LeadSource : 'unspecified');
};

/**
 * Get color for a specific source
 */
export const getSourceColor = (source: LeadSource | string): string => {
  const colors = getSourceColors();
  const normalizedSource = normalizeSource(source);
  return colors[normalizedSource] || DEFAULT_SOURCE_COLORS['unspecified'];
};

/**
 * Set color for a specific source
 */
export const setSourceColor = (source: LeadSource, color: string): void => {
  try {
    const colors = getSourceColors();
    colors[source] = color;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
  } catch (error) {
    console.error('Error saving source color to localStorage:', error);
  }
};

/**
 * Reset all colors to defaults
 */
export const resetSourceColors = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error resetting source colors:', error);
  }
};

/**
 * Reset color for a specific source to default
 */
export const resetSourceColor = (source: LeadSource): void => {
  try {
    const colors = getSourceColors();
    delete colors[source];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
  } catch (error) {
    console.error('Error resetting source color:', error);
  }
};
